import Alert from '@storeblocks/alert';
import { Form, Formik, FormikHelpers } from 'formik';
import { type FC } from 'react';
import { useSearchParams } from 'react-router';
import styled from 'styled-components';

import { useFundListQuery } from '@/api/funds/fundsApi';
import { OrderType } from '@/api/order/models/OrderType';
import { PostOrderResponse } from '@/api/order/models/PostOrderResponse';
import { usePostOrderMutation } from '@/api/order/orderApi';
import { mapPostFundOrderModelToDto } from '@/api/order/utils/mapPostFundOrderModelToDto';
import { useOrderListQuery } from '@/api/orderList/orderListApi';
import { Permission } from '@/api/permissions/models/Permission';
import { useOrganizationCmId } from '@/api/userSettings/hooks/useOrganizationCmId';
import { useUserSettings } from '@/api/userSettings/utils/useUserSettings';
import { useCurrentUserInfo } from '@/auth/hooks';
import { ContactForm } from '@/components/ContactForm/ContactForm';
import { Column, Row } from '@/components/Grid';
import { useHasPermission } from '@/components/Permissions/useHasPermission';
import { Show } from '@/components/Show';
import { WithGap } from '@/components/WithGap';
import { isE2E } from '@/config/utils';
import { useTexts } from '@/hooks/useTexts';
import { useOrderPageContext } from '@/pages/Order/OrderPageContext';

import { useOrderSteps } from '../../components/OrderStepsProvider';
import { useContactFormSource } from '../../hooks/useContactFormSource';
import { useIsCurrentUserOwner } from '../../hooks/useIsCurrentUserOwner';
import { FundOrder } from '../../types/Order';
import { getEarliestCutOffTime } from '../../utils/getEarliestCutOffTime';
import { CutoffInformationAlert } from '../OrderDetails/components/CutoffInformationAlert';
import { ConfirmInformationIsCorrectCheckbox } from './components/ConfirmInformationIsCorrectCheckbox';
import { NavigationButtons } from './components/NavigationButtons';
import { OrderDetailsTable } from './components/OrderDetailsTable';
import { PostFailedAlert } from './components/PostFailedAlert';
import { SwitchOrderDetailsTables } from './components/SwitchOrderDetailsTables';
import { UnitsInformationAlert } from './components/UnitsInformationAlert';
import { ConfirmFormValues } from './types/ConfirmFormValues';
import { validationSchema } from './utils/validationSchema';

const postError = 'post-error';

const initialFormValues: ConfirmFormValues = {
  confirmInformationIsCorrect: false,
};

export const FundOrderConfirm: FC = () => {
  const texts = useTexts();
  const [, setSearchParams] = useSearchParams();

  const organizationCmId = useOrganizationCmId();
  const orderListQuery = useOrderListQuery();

  const { data: fundList } = useFundListQuery();

  const isOwner = useIsCurrentUserOwner();
  const userInfo = useCurrentUserInfo();
  const { language } = useUserSettings();
  const hasPermission = useHasPermission();

  const {
    orderType,
    currentOrder,
    orderForSigning,
    signingCancelled,
    hasExistingOrder,
  } = useOrderPageContext<FundOrder>();
  const contactFormSource = useContactFormSource();

  const { completeCurrentStep } = useOrderSteps();

  const [postOrder] = usePostOrderMutation();

  if (!currentOrder) {
    throw new Error('Cannot use Confirm page without currentOrder.');
  }

  // Get the earliest cut-off time for the funds in the order.
  const cutOff = getEarliestCutOffTime(
    currentOrder.orderPlacements
      .map((placement) => placement.isin)
      .map((isin) => fundList!.byIsin[isin].cutOffTime),
  );

  const handleSubmit = async (
    _: ConfirmFormValues,
    formikHelpers: FormikHelpers<ConfirmFormValues>,
  ): Promise<void> => {
    try {
      let createdOrder: PostOrderResponse | undefined = undefined;

      if (!orderForSigning) {
        const dto = mapPostFundOrderModelToDto(
          organizationCmId,
          currentOrder,
          language,
        );

        createdOrder = await postOrder({ order: dto, orderType }).unwrap();
      }

      // Set the orderId in the url to allow the user to click the back button to edit their order.
      setSearchParams(
        {
          orderId: orderForSigning ? orderForSigning.id : createdOrder!.orderId,
        },
        { replace: true },
      );

      if (isE2E()) {
        // Don't redirect to signing when we are on E2E (Playwright) tests
        // because window.location.href would redirect the Playwright-controlled
        // browser and not the page under test.

        completeCurrentStep();
        return;
      }

      // Redirect to Signicat for signing if the user has order sign permission
      // and the current user is one of the signatories.
      if (
        hasPermission(Permission.OrdersSign) &&
        currentOrder.signatories.includes(userInfo.userCmId || '-1')
      ) {
        window.location.href = orderForSigning
          ? orderForSigning.signUrl
          : createdOrder!.signUrl;
        return;
      }

      // Refetch the order list to show the newly created order.
      orderListQuery.refetch();

      // Do not redirect and complete the current step if the user does not have order sign permission
      // or if the current user is not one of the signatories but complete the current step to immediately
      // show the completed step instead.
      completeCurrentStep();
    } catch {
      formikHelpers.setStatus(postError);
    }
  };

  return (
    <WithGap gap="64">
      <WithGap gap="48">
        <WithGap gap="24">
          <Row>
            <Column $span={9}>
              <ContactForm source={contactFormSource} />
            </Column>
          </Row>
          <h2>{texts.orders.form.steps.confirm.title}</h2>
        </WithGap>

        {(currentOrder.orderType === OrderType.Redemption ||
          currentOrder.orderType === OrderType.Subscription) && (
          <>
            <OrderDetailsTable
              portfolioShortName={currentOrder.portfolioId}
              orderPlacements={currentOrder.orderPlacements}
              payments={currentOrder.payments}
            />
          </>
        )}

        {currentOrder.orderType === OrderType.Switch && (
          <SwitchOrderDetailsTables
            portfolioShortName={currentOrder.portfolioId}
            transferToPortfolioNumber={currentOrder.transferToPortfolioNumber!}
            orderPlacements={currentOrder.orderPlacements}
          />
        )}

        <UnitsInformationAlert orderType={currentOrder.orderType} />

        {!!cutOff && (
          <CutoffInformationAlert
            orderType={orderType}
            cutOffTime={cutOff.cutOffTime}
            isPastCutoff={cutOff.isPastCutOff}
          />
        )}
      </WithGap>

      <Formik
        initialValues={initialFormValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema(texts)}
      >
        {({ status, isSubmitting }) => (
          <StyledForm>
            <WithGap gap="24">
              <ConfirmInformationIsCorrectCheckbox orderType={orderType} />

              <Show when={status === postError} animate>
                <PostFailedAlert />
              </Show>
            </WithGap>

            {signingCancelled && (
              <Alert
                data-test="signing-cancelled-alert"
                variant="info"
                title={texts.orders.form.steps.confirm.signingCancelledTitle}
                description={
                  texts.orders.form.steps.confirm.signingCancelledDescription
                }
              />
            )}

            <NavigationButtons
              isPending={isSubmitting}
              canGoBack={isOwner && !hasExistingOrder}
            />
          </StyledForm>
        )}
      </Formik>
    </WithGap>
  );
};

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  gap: 48px;
`;
